import { getApiBaseUrl, authenticatedFetch } from './apiClient'
import { encryptData, decryptData } from '@/utils/encryption'
import { getAgentEncryptionSettings } from '@/utils/agentEncryption'
import { v7 as uuidv7 } from 'uuid'
import { fetchDataSourceDirect } from '@/stores/dataSourceStore'
import { stripComments } from '@/utils/sqlParser'
import { notify } from '@/utils/notify'
import { makeBigIntSafe } from '@/utils/bigintSafe'

// Type definitions for command data structures
export interface BaseCommandData {
  nonce?: string
  timestamp?: number
  [key: string]: unknown
}

interface EncryptedPayloadBody {
  encrypted_payload: string
}

interface AgentCommand {
  type: string
  timeout: number
  data: BaseCommandData
}

interface AgentErrorResponse {
  status: 'error'
  message?: string
  error_code?: string
}

interface TableDataResponse {
  type: string
  rows?: Record<string, unknown>[]
  columns?: {
    name: string
    type: string
    [key: string]: unknown
  }[]
  [key: string]: unknown
}

interface ListResponse<T> {
  data?: T[]
  databases?: T[]
  tables?: T[]
  schemas?: T[]
  functions?: T[]
  procedures?: T[]
  triggers?: T[]
  views?: T[]
  indexes?: T[]
  sequences?: T[]
  events?: T[]
  constraints?: T[]
  columns?: T[]
  users?: T[]
  sessions?: T[]
  // ClickHouse/Milvus-specific arrays
  partitions?: T[]
  // Milvus-specific arrays
  aliases?: T[]
  resource_groups?: T[]
  privilege_groups?: T[]
  replicas?: T[]
  dictionaries?: T[]
  projections?: T[]
  roles?: T[]
  quotas?: T[]
  settings_profiles?: T[]
  named_collections?: T[]
  row_policies?: T[]
  mutations?: T[]
  merges?: T[]
  parts?: T[]
  backups?: T[]
  clusters?: T[]
  disks?: T[]
  [key: string]: T[] | undefined
}

// ClickHouse-specific response interfaces (exported for use in UI components)
export interface MutationResponse {
  mutation_id: string
  is_async: boolean
  status?: 'pending' | 'running' | 'completed' | 'failed'
}

interface EventDetailsResponse {
  type: 'get_event_details'
  event_details: unknown
}

interface ActiveSessionsResponse {
  type: 'get_active_sessions'
  data?: {
    sessions: {
      id: string | number
      user?: string
      database?: string
      [key: string]: unknown
    }[]
  }
}

// Type definition for encryption settings
interface EncryptionSettings {
  encryptionEnabled: boolean
  encryptionKey: string
}

// Type guard to check if environment variable is a valid string
function isValidString(value: string | undefined): value is string {
  return typeof value === 'string' && value.length > 0
}

// Type guard to check if response has error status
function isErrorResponse(response: unknown): response is AgentErrorResponse {
  return (
    response !== null &&
    typeof response === 'object' &&
    'status' in response &&
    (response as { status: unknown }).status === 'error'
  )
}

// Type guard to check if response is encrypted
function isEncryptedResponse(response: unknown): response is { encrypted_payload: string } {
  return (
    response !== null &&
    typeof response === 'object' &&
    'encrypted_payload' in response &&
    typeof (response as { encrypted_payload: unknown }).encrypted_payload === 'string'
  )
}

// ---------------------------------------------------------------------------
// Agent-unreachable failure callback
// ---------------------------------------------------------------------------
// When an agent-proxy request fails in a way that strongly suggests the agent
// is offline (network failure, 5xx, gateway timeout), we fire this callback so
// the connection store can trigger an immediate out-of-band health re-check
// instead of waiting for the next 10 s poll tick. Registered from
// useAgentHealthMonitor at the layout level.
type AgentFailureCallback = (agentId: string, error: unknown) => void
let agentFailureCallback: AgentFailureCallback | null = null

export function registerAgentFailureCallback(cb: AgentFailureCallback | null): void {
  agentFailureCallback = cb
}

// Classify whether an error from executeCommand indicates the agent is
// unreachable (vs a DB-level error, validation error, auth error, etc.).
// Conservative: we only signal offline on strong signals so we don't
// spam re-checks for every failed query.
function isAgentUnreachableError(error: unknown): boolean {
  if (!error) return false

  // Navigation aborts are not offline signals.
  if (error instanceof DOMException && error.name === 'AbortError') return false
  if (error instanceof Error && error.name === 'AbortError') return false

  // Fetch-level network failures (DNS, CORS, connection refused).
  if (error instanceof TypeError && /fetch/i.test(error.message)) return true

  const msg = error instanceof Error ? error.message : typeof error === 'string' ? error : ''
  // Some Error objects carry a `name` that is set to the backend's error_code
  // (see executeCommandInner's isErrorResponse branch). Treat common codes as
  // strong offline signals too.
  const name = error instanceof Error ? error.name : ''

  // HTTP error messages built by executeCommand start with "HTTP 5xx" / "HTTP 504" etc.
  if (/^HTTP 5\d\d/.test(msg)) return true

  // Common gateway / timeout / agent-unreachable signatures in the error body.
  if (/(bad gateway|gateway timeout|service unavailable|agent (is )?(offline|unreachable|not connected|disconnected|not available|not found|timeout)|no active agent|agent connection (closed|lost)|failed to (reach|contact|deliver.*to) agent)/i.test(msg)) {
    return true
  }

  // Backend error_code signals (mapped onto Error.name in executeCommandInner).
  if (/^(AGENT_|GATEWAY_|PROXY_|TIMEOUT$|TIMEOUT_)/i.test(name)) return true
  if (/(AGENT_OFFLINE|AGENT_UNREACHABLE|AGENT_NOT_CONNECTED|AGENT_NOT_FOUND|AGENT_TIMEOUT)/i.test(name)) return true

  return false
}

function notifyAgentFailure(agentId: string | undefined, error: unknown): void {
  if (!agentId || !agentFailureCallback) return
  if (!isAgentUnreachableError(error)) return
  try {
    agentFailureCallback(agentId, error)
  } catch (cbError) {
    console.warn('[AgentProxyService] agentFailureCallback threw:', cbError)
  }
}

/**
 * Centralized service for agent proxy operations
 * This replaces the individual service classes with a unified agent-based approach
 */
export class AgentProxyService {
  // Mutex for MongoDB session: ensures useDatabase() + command run as atomic pair
  private static mongoSessionLock: Promise<unknown> = Promise.resolve()

  private static async withMongoSession<T>(datasourceId: string, databaseName: string, fn: () => Promise<T>): Promise<T> {
    // Wait for any previous MongoDB operation to finish
    const previous = this.mongoSessionLock
    let release: (resolve: unknown) => void
    // oxlint-disable-next-line promise/avoid-new -- Deferred promise is required for mutex pattern
    this.mongoSessionLock = new Promise(resolve => { release = resolve })
    await previous
    try {
      await this.useDatabase(datasourceId, databaseName)
      return await fn()
    } finally {
      release!(undefined)
    }
  }

  /**
   * Get the regional API base URL for a datasource
   * Returns the region-specific URL if the datasource or agent has a region, otherwise returns null
   */
  static async getRegionalBaseUrl(datasourceId: string): Promise<string | null> {
    try {
      const datasource = await fetchDataSourceDirect(datasourceId)

      if (!datasource) {
        return null
      }

      // First priority: check if datasource has a region field
      // The API returns a region field on the datasource, but TypeScript types don't include it yet
      const datasourceWithRegion = datasource as typeof datasource & { region?: string }
      const { region: initialRegion } = datasourceWithRegion
      let region = initialRegion

      // Second priority: check agent metadata for region (fallback)
      if ((!region || region.trim() === '') && datasource.agentId) {
        try {
          // Import getAgentById dynamically to avoid circular dependencies.
          const { getAgentById } = await import('./apiClient')
          const { data: agent } = await getAgentById({ path: { agent_id: datasource.agentId }, throwOnError: true })
          const metadata = agent.metadata
          region = metadata ? (metadata.region as string | undefined) : undefined
        } catch (error) {
          console.warn('Failed to fetch agent for region:', error)
        }
      }

      // Validate region
      if (!region || region.trim() === '') {
        return null
      }

      // Skip regional URL for local development
      if (region.toLowerCase() === 'local') {
        return null
      }

      // Build regional URL: https://{region}-api.mazpin.com
      return `https://${region}-api.mazpin.com`
    } catch (error) {
      console.warn('Failed to get regional base URL:', error)
      return null
    }
  }

  /**
   * Execute a command through the agent proxy with proper encryption handling
   * @param datasourceId - The datasource ID
   * @param type - Command type
   * @param commandData - Command data
   * @param timeout - Timeout in seconds
   * @param suppressToast - Whether to suppress toast notifications
   * @param agentId - Optional agentId if already known (avoids extra API call for encryption settings)
   */
  private static async executeCommand(
    datasourceId: string,
    type: string,
    commandData: BaseCommandData,
    timeout = 30,
    suppressToast = false,
    agentId?: string
  ): Promise<unknown> {
    try {
      return await this.executeCommandInner(datasourceId, type, commandData, timeout, suppressToast, agentId)
    } catch (error) {
      // If we already know the agentId use it; otherwise try to resolve it
      // lazily (best-effort — don't throw a new error from the callback path).
      let resolvedAgentId = agentId
      if (!resolvedAgentId) {
        try {
          const ds = await fetchDataSourceDirect(datasourceId)
          resolvedAgentId = ds?.agentId
        } catch {
          // Ignore — we just won't fire the callback if we can't resolve.
        }
      }
      notifyAgentFailure(resolvedAgentId, error)
      throw error
    }
  }

  private static async executeCommandInner(
    datasourceId: string,
    type: string,
    commandData: BaseCommandData,
    timeout = 30,
    suppressToast = false,
    agentId?: string
  ): Promise<unknown> {
    // Get encryption settings for the datasource (pass agentId if available to avoid extra API call).
    const { encryptionEnabled, encryptionKey } = await this.getEncryptionSettings(datasourceId, agentId)

    // Get regional base URL for agent-proxy requests.
    const regionalBaseUrl = await this.getRegionalBaseUrl(datasourceId)
    
    const command: AgentCommand = {
      type,
      timeout,
      data: {
        ...commandData,
        nonce: uuidv7(),
        timestamp: Math.floor(Date.now() / 1000)
      }
    }



    let requestBody: EncryptedPayloadBody

    if (encryptionEnabled && encryptionKey) {
      // Encrypt the command. Convert to plain object for encryption.
      const commandObj: Record<string, unknown> = {
        type: command.type,
        timeout: command.timeout,
        data: command.data
      }
      const encryptedPayload = await encryptData(commandObj, encryptionKey)
      requestBody = {
        encrypted_payload: encryptedPayload
      }
    } else {
      // For unencrypted requests (fallback).
      requestBody = {
        encrypted_payload: JSON.stringify(command)
      }
    }

    // Log the agent proxy command type for debugging purposes.
    console.log(`🔍 [AgentProxyService] Executing command: ${type} for datasource: ${datasourceId}`)

    // Determine which base URL to use.
    const baseUrl = regionalBaseUrl || getApiBaseUrl()
    if (regionalBaseUrl) {
      console.log(`🌍 [AgentProxyService] Using regional base URL: ${regionalBaseUrl} for datasource: ${datasourceId}`)
    }

    // Make direct fetch call using resolved base URL with token refresh + retry.
    const url = `${baseUrl}/api/agent-proxy/${datasourceId}`
    let fetchResponse: Response
    try {
      fetchResponse = await authenticatedFetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'agent-proxy-command': type
        },
        body: JSON.stringify(requestBody),
        credentials: 'include'
      })
    } catch (error) {
      // Handle abort errors gracefully. These happen when the page navigates away
      // Or the browser context is closed (common in parallel test execution).
      // Note: Only DOMException with AbortError name is a true abort.
      // TypeError "Failed to fetch" can be network errors, CORS, DNS issues, etc.

      const isAbort = error instanceof DOMException && error.name === 'AbortError'

      if (isAbort) {
        // Don't spam console with these errors as they're expected during navigation.
        console.debug(`[AgentProxyService] Request aborted for ${type} (likely page navigation)`)
        // Throw a specific error that callers can identify and handle.
        const abortError = new Error('Request aborted due to navigation')
        abortError.name = 'AbortError'
        throw abortError
      }
      throw error
    }

    // Handle HTTP errors.
    if (!fetchResponse.ok) {
      const errorText = await fetchResponse.text()
      let errorMessage = `HTTP ${fetchResponse.status}: ${fetchResponse.statusText}`
      try {
        const errorJson = JSON.parse(errorText) as unknown
        if (errorJson && typeof errorJson === 'object') {
          const errorObj = errorJson as Record<string, unknown>
          errorMessage = (typeof errorObj.message === 'string' ? errorObj.message : '') ||
                        (typeof errorObj.error === 'string' ? errorObj.error : '') ||
                        errorMessage
        }
      } catch {
        // If response is not JSON, use the text as-is.
        if (errorText) {
          errorMessage = errorText
        }
      }
      throw new Error(errorMessage)
    }

    // Parse response with BigInt-safe handling.
    // Large Int64 values (e.g., Milvus auto-generated IDs) exceed Number.MAX_SAFE_INTEGER
    // and lose precision when parsed as JavaScript numbers. Convert them to strings.
    const responseText = await fetchResponse.text()
    const safeResponseText = makeBigIntSafe(responseText)
    const response = JSON.parse(safeResponseText) as unknown

    // Handle encrypted response if needed.
    let finalResponse: unknown

    // Always try to decrypt if we get an encrypted response, regardless of encryption settings.
    if (isEncryptedResponse(response)) {
      // If response is encrypted, we MUST have an encryption key.
      if (!encryptionKey) {
        console.error(`🔍 [AgentProxyService] Received encrypted response but no encryption key available for datasource: ${datasourceId}`)
        throw new Error('Received encrypted response but encryption key is not configured. Please check your agent encryption settings.')
      }
      finalResponse = await this.decryptResponse(response, encryptionKey)
    } else if (encryptionEnabled && encryptionKey) {
      finalResponse = await this.decryptResponse(response, encryptionKey)
    } else {
      finalResponse = response
    }

    // Check for agent proxy errors and show user-friendly messages.
    if (isErrorResponse(finalResponse)) {
      const errorMessage = finalResponse.message || 'An unexpected error occurred'
      const errorCode = finalResponse.error_code || 'UNKNOWN_ERROR'

      // Only show toast if not suppressed.
      if (!suppressToast) {
        notify.error(`Error: ${errorMessage}`)
      }

      // Throw error to maintain existing error handling in components.
      // Attach additional diagnostic fields (e.g., dependencies/remediation) for richer UI handling.
      const error = new Error(errorMessage) as Error & Record<string, unknown>
      error.name = errorCode
      // Access properties safely - isErrorResponse type guard ensures finalResponse is an object.
      if ('dependencies' in finalResponse && finalResponse.dependencies !== undefined) {
        error.dependencies = finalResponse.dependencies
      }
      if ('remediation' in finalResponse && finalResponse.remediation !== undefined) {
        error.remediation = finalResponse.remediation
      }
      if ('type' in finalResponse && finalResponse.type !== undefined) {
        error.type = finalResponse.type
      }
      throw error
    }

    return finalResponse
  }

  /**
   * Get encryption settings for a datasource
   * @param datasourceId - The datasource ID
   * @param agentId - Optional agentId if already known (avoids extra API call)
   */
  private static async getEncryptionSettings(datasourceId: string, agentId?: string): Promise<EncryptionSettings> {
    // Default to disabled encryption.
    let encryptionEnabled = false
    let encryptionKey = ''

    // Use provided agentId or fetch from datasource.
    let resolvedAgentId = agentId

    if (!resolvedAgentId) {
      try {
        const datasource = await fetchDataSourceDirect(datasourceId)
        resolvedAgentId = datasource?.agentId
      } catch (error) {
        // If we can't fetch the datasource and no agentId was provided, throw an error.
        // This prevents silent failures when the server is under load.
        throw new Error(`Failed to fetch datasource ${datasourceId} for encryption settings: ${error instanceof Error ? error.message : 'Unknown error'}`, { cause: error })
      }
    }

    if (resolvedAgentId) {
      const agentEncryption = getAgentEncryptionSettings(resolvedAgentId)
      // Use agent-specific encryption settings.
      encryptionEnabled = agentEncryption.enabled
      if (isValidString(agentEncryption.key)) {
        encryptionKey = agentEncryption.key
      }
    } else {
      console.warn(`🔍 [AgentProxyService] No agentId found for datasource ${datasourceId} - encryption may fail`)
    }

    return { encryptionEnabled, encryptionKey }
  }

  /**
   * Decrypt response if needed and normalize format.
   */
  private static async decryptResponse(response: unknown, encryptionKey: string): Promise<unknown> {
    let decryptedData: unknown = response

    // Handle encrypted response case.
    if (isEncryptedResponse(response)) {
      // Validate encryption key is available for encrypted responses.
      if (!encryptionKey) {
        console.error(`🔍 [AgentProxyService] Received encrypted response but no encryption key available`)
        throw new Error('Received encrypted response but encryption key is not configured. Please check your agent encryption settings.')
      }
      try {
        const decrypted = await decryptData(response.encrypted_payload, encryptionKey)
        decryptedData = typeof decrypted === 'string' ? JSON.parse(makeBigIntSafe(decrypted)) as unknown : decrypted
      } catch (error) {
        console.error(`🔍 [AgentProxyService] Decryption failed:`, error)
        const errorMessage = error instanceof Error ? error.message : 'Unknown decryption error'
        throw new Error(`Failed to decrypt response: ${errorMessage}. Please verify your encryption key is correct.`, { cause: error })
      }
    } else if (typeof response === 'string' && encryptionKey) {
      // Only attempt decryption if we have an encryption key.
      try {
        const decrypted = await decryptData(response, encryptionKey)
        decryptedData = typeof decrypted === 'string' ? JSON.parse(makeBigIntSafe(decrypted)) as unknown : decrypted
      } catch (error) {
        console.error(`🔍 [AgentProxyService] Decryption failed:`, error)
        // For non-encrypted responses that failed to parse, return original.
        decryptedData = response
      }
    }

    // Normalize response format for consistency.
    return this.normalizeResponse(decryptedData)
  }

  /**
   * Normalize response format to ensure consistency.
   */
  private static normalizeResponse(response: unknown): unknown {
    if (!response) return response

    // If response is already an array, return as-is.
    if (Array.isArray(response)) {
      return response
    }

    // Handle get_column_info response format specifically.
    if (typeof response === 'object' && 'type' in response && (response as { type: string }).type === 'get_column_info') {
      const columnResponse = response as { columns?: unknown[] }
      if (columnResponse.columns && Array.isArray(columnResponse.columns)) {
        return columnResponse.columns
      }
    }

    // For complex responses that need to preserve structure (table data, etc.)
    // Check if this is a structured response that should be kept intact.
    const typedResponse = response as { type?: string }
    if (typedResponse.type && (
      typedResponse.type === 'get_table_data' ||
      typedResponse.type === 'get_table_schema' ||
      typedResponse.type === 'execute_query' ||
      typedResponse.type === 'explain_query_plan' ||
      typedResponse.type === 'get_sequence_definition' ||
      typedResponse.type === 'drop_sequence' ||
      typedResponse.type === 'get_service_levels' ||
      typedResponse.type === 'get_roles' ||
      typedResponse.type === 'get_collection_stats'
    )) {
      // Return structured responses as-is to preserve rows, columns, pagination, etc.
      return response
    }

    // Handle get_event_details response format specifically.
    if (typeof response === 'object' && 'type' in response && (response as { type: string }).type === 'get_event_details') {
      const eventResponse = response as EventDetailsResponse
      if (eventResponse.event_details) {
        return eventResponse.event_details
      }
    }

    // Handle get_active_sessions response format specifically.
    if (typeof response === 'object' && 'type' in response && (response as { type: string }).type === 'get_active_sessions') {
      const sessionsResponse = response as ActiveSessionsResponse
      if (sessionsResponse.data?.sessions && Array.isArray(sessionsResponse.data.sessions)) {
        return sessionsResponse.data.sessions
      }
    }

    // If response has data property with array, extract it for simple list responses.
    if (typeof response === 'object' && 'data' in response) {
      const listResponse = response as ListResponse<unknown>
      if (listResponse.data && Array.isArray(listResponse.data)) {
        return listResponse.data
      }
    }

    // Handle specific agent response formats for simple list operations.
    const listResponse = response as ListResponse<unknown>
    if (listResponse.databases && Array.isArray(listResponse.databases)) {
      return listResponse.databases
    }
    if (listResponse.tables && Array.isArray(listResponse.tables)) {
      return listResponse.tables
    }
    if (listResponse.schemas && Array.isArray(listResponse.schemas)) {
      return listResponse.schemas
    }
    if (listResponse.functions && Array.isArray(listResponse.functions)) {
      return listResponse.functions
    }
    if (listResponse.procedures && Array.isArray(listResponse.procedures)) {
      return listResponse.procedures
    }
    if (listResponse.triggers && Array.isArray(listResponse.triggers)) {
      return listResponse.triggers
    }
    if (listResponse.views && Array.isArray(listResponse.views)) {
      return listResponse.views
    }
    if (listResponse.indexes && Array.isArray(listResponse.indexes)) {
      return listResponse.indexes
    }
    if (listResponse.sequences && Array.isArray(listResponse.sequences)) {
      return listResponse.sequences
    }
    if (listResponse.events && Array.isArray(listResponse.events)) {
      return listResponse.events
    }
    if (listResponse.constraints && Array.isArray(listResponse.constraints)) {
      return listResponse.constraints
    }
    // Only extract columns array if this is not a structured response.
    const tableResponse = response as TableDataResponse
    if (listResponse.columns && Array.isArray(listResponse.columns) && !tableResponse.rows) {
      return listResponse.columns
    }
    if (listResponse.users && Array.isArray(listResponse.users)) {
      return listResponse.users
    }
    if (listResponse.sessions && Array.isArray(listResponse.sessions)) {
      return listResponse.sessions
    }
    // ClickHouse-specific arrays
    if (listResponse.partitions && Array.isArray(listResponse.partitions)) {
      return listResponse.partitions
    }
    if (listResponse.dictionaries && Array.isArray(listResponse.dictionaries)) {
      return listResponse.dictionaries
    }
    if (listResponse.projections && Array.isArray(listResponse.projections)) {
      return listResponse.projections
    }
    if (listResponse.roles && Array.isArray(listResponse.roles)) {
      return listResponse.roles
    }
    if (listResponse.quotas && Array.isArray(listResponse.quotas)) {
      return listResponse.quotas
    }
    if (listResponse.settings_profiles && Array.isArray(listResponse.settings_profiles)) {
      return listResponse.settings_profiles
    }
    if (listResponse.named_collections && Array.isArray(listResponse.named_collections)) {
      return listResponse.named_collections
    }
    if (listResponse.row_policies && Array.isArray(listResponse.row_policies)) {
      return listResponse.row_policies
    }
    if (listResponse.mutations && Array.isArray(listResponse.mutations)) {
      return listResponse.mutations
    }
    if (listResponse.merges && Array.isArray(listResponse.merges)) {
      return listResponse.merges
    }
    if (listResponse.parts && Array.isArray(listResponse.parts)) {
      return listResponse.parts
    }
    if (listResponse.backups && Array.isArray(listResponse.backups)) {
      return listResponse.backups
    }
    if (listResponse.clusters && Array.isArray(listResponse.clusters)) {
      return listResponse.clusters
    }
    if (listResponse.disks && Array.isArray(listResponse.disks)) {
      return listResponse.disks
    }
    // Milvus-specific arrays
    if (listResponse.aliases && Array.isArray(listResponse.aliases)) {
      return listResponse.aliases
    }
    if (listResponse.resource_groups && Array.isArray(listResponse.resource_groups)) {
      return listResponse.resource_groups
    }
    if (listResponse.privilege_groups && Array.isArray(listResponse.privilege_groups)) {
      return listResponse.privilege_groups
    }
    if (listResponse.replicas && Array.isArray(listResponse.replicas)) {
      return listResponse.replicas
    }

    // Return as-is if no normalization needed.
    return response
  }

  // Database operations.
  static async getDatabases(datasourceId: string, limit?: number, page?: number) {
    return this.executeCommand(datasourceId, 'get_databases', {
      limit: limit || 100,
      page: page || 1
    })
  }

  static async getSchemas(datasourceId: string, databaseName: string) {
    return this.executeCommand(datasourceId, 'get_schemas', {
      database_name: databaseName,
      limit: 100
    })
  }

  static async getTables(datasourceId: string, databaseName: string, schemaName?: string, _dbType?: string) {
    const commandData = { database_name: databaseName, schema_name: schemaName, limit: 500 }
    return this.executeCommand(datasourceId, 'get_tables', commandData)
  }

  static async getTableSchema(datasourceId: string, databaseName: string, tableName: string, schemaName?: string, suppressToast = false, dbType?: string) {
    const commandData = { database_name: databaseName, table_name: tableName, schema_name: schemaName }

    if (dbType === 'mongodb' && databaseName) {
      return this.withMongoSession(datasourceId, databaseName, async () =>
        this.executeCommand(datasourceId, 'get_table_schema', commandData, 30, suppressToast)
      )
    }

    return this.executeCommand(datasourceId, 'get_table_schema', commandData, 30, suppressToast)
  }

  /**
   * Get Redis key info in a single request (type, ttl, value, slot).
   * This combines TYPE, TTL, GET/HGETALL/etc., and CLUSTER KEYSLOT into one call.
   */
  static async getRedisKeyInfo(
    datasourceId: string,
    keyName: string,
    options?: { includeSlot?: boolean }
  ): Promise<{
    type: string
    ttl: number
    slot?: number | null
    value: unknown
    encoding?: string
    memory_usage?: number | null
  }> {
    const response = await this.executeCommand(datasourceId, 'get_table_schema', {
      table_name: keyName,
      include_value: true,
      include_slot: options?.includeSlot ?? false
    }) as {
      schema?: {
        table_type?: string
        type?: string
        ttl?: number
        slot?: number | null
        value?: unknown
        encoding?: string
        memory_usage?: number | null
      }
    }

    const schema = response.schema ?? {}
    return {
      type: schema.table_type ?? schema.type ?? 'none',
      ttl: schema.ttl ?? -2,
      slot: schema.slot,
      value: schema.value,
      encoding: schema.encoding,
      memory_usage: schema.memory_usage
    }
  }

  static async createDatabase(datasourceId: string, databaseName: string) {
    return this.executeCommand(datasourceId, 'create_database', {
      database_name: databaseName
    })
  }

  static async dropDatabase(datasourceId: string, databaseName: string) {
    return this.executeCommand(datasourceId, 'drop_database', {
      database_name: databaseName
    })
  }

  // Schema operations.
  static async createSchema(datasourceId: string, databaseName: string, schemaData: BaseCommandData) {
    return this.executeCommand(datasourceId, 'create_schema', {
      database_name: databaseName,
      ...schemaData
    })
  }

  static async dropSchema(datasourceId: string, databaseName: string, schemaName: string, cascade?: boolean) {
    return this.executeCommand(datasourceId, 'drop_schema', {
      database_name: databaseName,
      schema_name: schemaName,
      cascade: cascade
    })
  }

  // Table operations.
  static async createTable(datasourceId: string, databaseName: string, tableData: BaseCommandData) {
    return this.executeCommand(datasourceId, 'create_table', {
      database_name: databaseName,
      ...tableData
    })
  }

  static async dropTable(datasourceId: string, databaseName: string, tableName: string, options?: {
    schemaName?: string
    cascade?: boolean
  }) {
    return this.executeCommand(datasourceId, 'drop_table', {
      database_name: databaseName,
      table_name: tableName,
      schema_name: options?.schemaName,
      cascade: options?.cascade
    })
  }

  static async renameTable(datasourceId: string, databaseName: string, tableName: string, newTableName: string, options?: {
    schemaName?: string
  }) {
    return this.executeCommand(datasourceId, 'rename_table', {
      database_name: databaseName,
      table_name: tableName,
      new_table_name: newTableName,
      schema_name: options?.schemaName
    })
  }

  static async getTableData(datasourceId: string, databaseName: string, tableName: string, options?: {
    schemaName?: string
    limit?: number
    page?: number
    // Explicit row offset. When provided, the agent uses `offset` as the
    // authoritative skip; otherwise it falls back to `(page - 1) * limit`.
    // Sending both `page` and `offset` together is supported, but if you
    // change one you MUST change the other in lockstep — otherwise the
    // client-side N+1 lookahead (limit = pageSize + 1) will silently skip
    // the previous page's lookahead row at every page boundary.
    offset?: number
    where_clause?: string
    // String for SQL DBs, object for MongoDB.
    order_by?: string | Record<string, number>
    order_column?: string
    order_direction?: string
    // MongoDB-specific parameters:
    // - filter: MongoDB filter query object.
    // - sort: MongoDB sort object { field: 1 } or { field: -1 }.
    filter?: Record<string, unknown>
    sort?: Record<string, number>
    suppressToast?: boolean
    // Database type for MongoDB session switching.
    dbType?: string
  }) {
    const commandData = {
      database_name: databaseName,
      table_name: tableName,
      schema_name: options?.schemaName,
      limit: options?.limit || 100,
      page: options?.page || 1,
      offset: options?.offset,
      where_clause: options?.where_clause,
      order_by: options?.order_by,
      order_column: options?.order_column,
      order_direction: options?.order_direction,
      filter: options?.filter,
      sort: options?.sort
    }

    // MongoDB needs session switching via mutex to avoid race conditions
    if (options?.dbType === 'mongodb' && databaseName) {
      return this.withMongoSession(datasourceId, databaseName, async () =>
        this.executeCommand(datasourceId, 'get_table_data', commandData, 30, options.suppressToast || false)
      )
    }

    return this.executeCommand(datasourceId, 'get_table_data', commandData, 30, options?.suppressToast || false)
  }

  // Data operations.
  static async insertRows(datasourceId: string, databaseName: string, tableName: string, data: unknown[], options?: {
    schemaName?: string
    // Database type for MongoDB session switching.
    dbType?: string
  }) {
    const isMongoDB = options?.dbType === 'mongodb'

    const commandData = {
      database_name: databaseName,
      table_name: tableName,
      schema_name: options?.schemaName,
      rows: data
    }

    if (isMongoDB && databaseName) {
      return this.withMongoSession(datasourceId, databaseName, async () =>
        this.executeCommand(datasourceId, 'insert_rows', commandData)
      )
    }

    return this.executeCommand(datasourceId, 'insert_rows', commandData)
  }

  static async updateRows(datasourceId: string, databaseName: string, tableName: string, data: unknown[] | Record<string, unknown>, whereConditions: Record<string, unknown>, options?: {
    schemaName?: string
    rowId?: string
    // Database type for format selection (e.g., 'mongodb').
    dbType?: string
  }) {
    const rowData = Array.isArray(data) ? data[0] : data

    // If rowId is provided, update where conditions with the primary key.
    let finalWhereConditions = whereConditions
    if (options?.rowId) {
      // Find primary key name from row_data and use it for where_conditions.
      const rowDataObj = rowData as Record<string, unknown>
      const primaryKeyName = Object.keys(rowDataObj).find(key =>
        key.toLowerCase().includes('id') || key.toLowerCase().includes('_id')
      ) || 'id'
      finalWhereConditions = { [primaryKeyName]: options.rowId }
    }

    // Different databases use different field names.
    const isMongoDB = options?.dbType === 'mongodb'

    const isClickHouse = options?.dbType === 'clickhouse'

    // For MongoDB, exclude _id from update data (MongoDB doesn't allow modifying _id).
    let updateData = rowData
    if (isMongoDB && rowData && typeof rowData === 'object') {
       
      const { _id, ...restData } = rowData as Record<string, unknown>
      updateData = restData
    }

    // Build WHERE clause string for ClickHouse from where_conditions object.
    const buildWhereClause = (conditions: Record<string, unknown>): string => {
      return Object.entries(conditions)
        .map(([key, value]) => {
          if (typeof value === 'string') {
            return `${key} = '${value}'`
          }
          return `${key} = ${String(value)}`
        })
        .join(' AND ')
    }

    let commandData: BaseCommandData
    if (isMongoDB) {
      // MongoDB requires $set operator.
      commandData = {
        database_name: databaseName,
        table_name: tableName,
        schema_name: options.schemaName,
        filter: finalWhereConditions,
        update_data: { $set: updateData }
      }
    } else if (isClickHouse) {
      // ClickHouse uses set_values and where (string).
      // Exclude WHERE columns from set_values (can't update ORDER BY/key columns).
      const whereKeys = Object.keys(finalWhereConditions)
      const rowDataObj = rowData as Record<string, unknown>
      const setValues = Object.fromEntries(
        Object.entries(rowDataObj).filter(([key]) => !whereKeys.includes(key))
      )

      // Edge case: if all columns are key columns, there's nothing to update.
      if (Object.keys(setValues).length === 0) {
        throw new Error('Cannot update row: all columns are key columns (ORDER BY/PRIMARY KEY). No values to set.')
      }

      commandData = {
        database_name: databaseName,
        table_name: tableName,
        schema_name: options.schemaName,
        set_values: setValues,
        where: buildWhereClause(finalWhereConditions)
      }
    } else {
      // Default format for other databases.
      commandData = {
        database_name: databaseName,
        table_name: tableName,
        schema_name: options?.schemaName,
        row_data: rowData,
        where_conditions: finalWhereConditions
      }
    }

    if (isMongoDB && databaseName) {
      return this.withMongoSession(datasourceId, databaseName, async () =>
        this.executeCommand(datasourceId, 'update_rows', commandData)
      )
    }

    return this.executeCommand(datasourceId, 'update_rows', commandData)
  }

  static async deleteRows(datasourceId: string, databaseName: string, tableName: string, whereConditions: Record<string, unknown>, options?: {
    schemaName?: string
    // Database type for format selection (e.g., 'mongodb').
    dbType?: string
  }) {
    // MongoDB uses 'filter' instead of 'where_conditions'.
    const isMongoDB = options?.dbType === 'mongodb'

    const commandData = {
      database_name: databaseName,
      table_name: tableName,
      schema_name: options?.schemaName,
      ...(isMongoDB
        ? { filter: whereConditions }
        : { where_conditions: whereConditions }
      )
    }

    if (isMongoDB && databaseName) {
      return this.withMongoSession(datasourceId, databaseName, async () =>
        this.executeCommand(datasourceId, 'delete_rows', commandData)
      )
    }

    return this.executeCommand(datasourceId, 'delete_rows', commandData)
  }

  // Query operations.
  static async executeQuery(datasourceId: string, query: string, databaseName?: string, suppressToast = false, connectionType?: string) {
    // MongoDB's execute_query doesn't honor database_name for shell commands.
    // MongoDB: use mutex to ensure useDatabase() + execute_query run atomically
    if (connectionType === 'mongodb' && databaseName) {
      return this.withMongoSession(datasourceId, databaseName, async () =>
        this.executeCommand(datasourceId, 'execute_query', {
          query,
          database_name: databaseName
        }, 30, suppressToast)
      )
    }

    return this.executeCommand(datasourceId, 'execute_query', {
      query,
      database_name: databaseName
    }, 30, suppressToast)
  }

  static async analyzeTable(datasourceId: string, databaseName: string, tableName: string, suppressToast = false) {
    return this.executeCommand(datasourceId, 'analyze_table', {
      database_name: databaseName,
      table_name: tableName
    }, 30, suppressToast)
  }

  /**
   * Execute a Milvus JSON command from the query editor.
   * Parses the JSON query to extract `type` and `data`, then sends via executeCommand.
   */
  static async executeMilvusCommand(datasourceId: string, jsonQuery: string, databaseName?: string, suppressToast = false) {
    let parsed: { type?: string; data?: Record<string, unknown>; timeout?: number }
    try {
      parsed = JSON.parse(jsonQuery) as typeof parsed
    } catch {
      throw new Error('Invalid JSON command. Please check your query syntax.')
    }
    const commandType = parsed.type ?? 'execute_query'
    const commandData: Record<string, unknown> = parsed.data && typeof parsed.data === 'object' ? { ...parsed.data } : {}
    if (databaseName && !commandData.database_name) {
      commandData.database_name = databaseName
    }
    return this.executeCommand(datasourceId, commandType, commandData, parsed.timeout ?? 30, suppressToast)
  }

  static async explainQueryPlan(datasourceId: string, query: string, databaseName?: string, suppressToast = false, connectionType?: string) {
    const cleanQuery = stripComments(query)
    const commandData = { query: cleanQuery, database_name: databaseName }

    if (connectionType === 'mongodb' && databaseName) {
      return this.withMongoSession(datasourceId, databaseName, async () =>
        this.executeCommand(datasourceId, 'explain_query_plan', commandData, 30, suppressToast)
      )
    }

    return this.executeCommand(datasourceId, 'explain_query_plan', commandData, 30, suppressToast)
  }

  // Session operations.
  static async getActiveSessions(datasourceId: string, databaseFilter?: string) {
    return this.executeCommand(datasourceId, 'get_active_sessions', {
      database_filter: databaseFilter
    })
  }

  static async killSession(datasourceId: string, sessionId: string) {
    return this.executeCommand(datasourceId, 'kill_session', {
      session_id: sessionId
    })
  }

  // Event operations.
  static async getEvents(datasourceId: string, databaseName: string) {
    return this.executeCommand(datasourceId, 'get_events', {
      database_name: databaseName,
      limit: 500
    })
  }

  static async createEvent(datasourceId: string, databaseName: string, eventData: BaseCommandData) {
    return this.executeCommand(datasourceId, 'create_event', {
      database_name: databaseName,
      ...eventData
    })
  }

  static async getEventDetails(datasourceId: string, databaseName: string, eventName: string) {
    return this.executeCommand(datasourceId, 'get_event_details', {
      database_name: databaseName,
      event_name: eventName
    })
  }

  static async dropEvent(datasourceId: string, databaseName: string, eventName: string) {
    return this.executeCommand(datasourceId, 'drop_event', {
      database_name: databaseName,
      event_name: eventName
    })
  }

  static async alterEvent(datasourceId: string, databaseName: string, eventName: string, eventData: BaseCommandData) {
    return this.executeCommand(datasourceId, 'alter_event', {
      database_name: databaseName,
      event_name: eventName,
      ...eventData
    })
  }

  // Column operations.
  static async getColumnInfo(datasourceId: string, databaseName: string, tableName: string, schemaName?: string, suppressToast = false, dbType?: string) {
    const commandData = { database_name: databaseName, table_name: tableName, schema_name: schemaName }

    if (dbType === 'mongodb' && databaseName) {
      return this.withMongoSession(datasourceId, databaseName, async () =>
        this.executeCommand(datasourceId, 'get_column_info', commandData, 30, suppressToast)
      )
    }

    return this.executeCommand(datasourceId, 'get_column_info', commandData, 30, suppressToast)
  }

  static async addColumn(datasourceId: string, databaseName: string, tableName: string, columnData: BaseCommandData, schemaName?: string, databaseType?: string, suppressToast = false) {
    // ScyllaDB expects column_name and column_type at the top level
    // Other databases expect a nested column object
    if (databaseType === 'ScyllaDB') {
      const { name, type } = columnData as { name: string; type: string };
      return this.executeCommand(datasourceId, 'add_column', {
        database_name: databaseName,
        table_name: tableName,
        column_name: name,
        column_type: type
      }, 30, suppressToast)
    }
    return this.executeCommand(datasourceId, 'add_column', {
      database_name: databaseName,
      table_name: tableName,
      schema_name: schemaName,
      column: columnData
    }, 30, suppressToast)
  }

  static async modifyColumn(datasourceId: string, databaseName: string, tableName: string, columnName: string, newColumnData: BaseCommandData, schemaName?: string, suppressToast = false) {
    return this.executeCommand(datasourceId, 'modify_column', {
      database_name: databaseName,
      table_name: tableName,
      schema_name: schemaName,
      column_name: columnName,
      new_column: newColumnData
    }, 30, suppressToast)
  }

  static async dropColumn(datasourceId: string, databaseName: string, tableName: string, columnName: string, schemaName?: string, suppressToast = false) {
    return this.executeCommand(datasourceId, 'drop_column', {
      database_name: databaseName,
      table_name: tableName,
      schema_name: schemaName,
      column_name: columnName
    }, 30, suppressToast)
  }

  // Index operations.
  static async createIndex(datasourceId: string, databaseName: string, indexData: BaseCommandData) {
    return this.executeCommand(datasourceId, 'create_index', {
      database_name: databaseName,
      ...indexData
    })
  }

  static async dropIndex(datasourceId: string, databaseName: string, indexName: string, schemaName?: string, tableName?: string) {
    return this.executeCommand(datasourceId, 'drop_index', {
      database_name: databaseName,
      index_name: indexName,
      schema_name: schemaName,
      ...(tableName && { table_name: tableName })
    })
  }

  static async getIndexes(datasourceId: string, databaseName: string, schemaName?: string) {
    return this.executeCommand(datasourceId, 'get_indexes', {
      database_name: databaseName,
      schema_name: schemaName,
      limit: 500
    })
  }

  static async getTableIndexes(datasourceId: string, databaseName: string, tableName: string) {
    return this.executeCommand(datasourceId, 'get_table_indexes', {
      database_name: databaseName,
      table_name: tableName,
      limit: 500
    })
  }

  static async getIndexDefinition(datasourceId: string, databaseName: string, indexName: string, schemaName?: string, tableName?: string) {
    return this.executeCommand(datasourceId, 'get_index_definition', {
      database_name: databaseName,
      index_name: indexName,
      schema_name: schemaName,
      ...(tableName && { table_name: tableName })
    })
  }

  // User operations.
  static async getDatabaseUsers(datasourceId: string, databaseName: string) {
    return this.executeCommand(datasourceId, 'get_database_users', {
      database_name: databaseName,
      limit: 500
    })
  }

  static async createUser(datasourceId: string, databaseName: string, userData: BaseCommandData) {
    return this.executeCommand(datasourceId, 'create_user', {
      database_name: databaseName,
      ...userData
    })
  }

  static async alterUser(datasourceId: string, databaseName: string, username: string, userData: BaseCommandData) {
    return this.executeCommand(datasourceId, 'alter_user', {
      database_name: databaseName,
      username,
      ...userData
    })
  }

  static async dropUser(datasourceId: string, databaseName: string, username: string) {
    return this.executeCommand(datasourceId, 'drop_user', {
      database_name: databaseName,
      username
    })
  }

  static async getUserDetails(datasourceId: string, databaseName: string, username: string, includeDependencies?: boolean) {
    return this.executeCommand(datasourceId, 'get_user_details', {
      database_name: databaseName,
      username,
      include_dependencies: includeDependencies
    })
  }

  // Remove user membership from role (PostgreSQL specific).
  static async removeUserMembership(datasourceId: string, databaseName: string, username: string, roleName: string) {
    return this.executeCommand(datasourceId, 'remove_user_membership', {
      database_name: databaseName,
      username,
      role_name: roleName
    })
  }

  // Reassign owned objects from one user to another (PostgreSQL specific).
  static async reassignOwnedBy(datasourceId: string, databaseName: string, fromUsername: string, toUsername: string) {
    // Send both username- and role-style fields for maximum backend compatibility.
    return this.executeCommand(datasourceId, 'reassign_owned_by', {
      database_name: databaseName,
      from_username: fromUsername,
      to_username: toUsername,
      // Some agent versions expect role field names.
      from_role: fromUsername,
      to_role: toUsername
    })
  }

  // Functions.
  static async getFunctions(datasourceId: string, databaseName: string, schemaName?: string) {
    return this.executeCommand(datasourceId, 'get_functions', {
      database_name: databaseName,
      schema_name: schemaName,
      limit: 500
    })
  }

  static async getFunctionDefinition(datasourceId: string, databaseName: string, functionName: string, schemaName?: string, functionArguments?: string) {
    return this.executeCommand(datasourceId, 'get_function_definition', {
      database_name: databaseName,
      function_name: functionName,
      schema_name: schemaName,
      ...(functionArguments ? { arguments: functionArguments } : {})
    })
  }

  // Procedures.
  static async getProcedures(datasourceId: string, databaseName: string, schemaName?: string) {
    return this.executeCommand(datasourceId, 'get_procedures', {
      database_name: databaseName,
      schema_name: schemaName,
      limit: 500
    })
  }

  static async getProcedureDefinition(datasourceId: string, databaseName: string, procedureName: string, schemaName?: string) {
    return this.executeCommand(datasourceId, 'get_procedure_definition', {
      database_name: databaseName,
      procedure_name: procedureName,
      schema_name: schemaName
    })
  }

  // Triggers.
  static async getTriggers(datasourceId: string, databaseName: string, schemaName?: string) {
    return this.executeCommand(datasourceId, 'get_triggers', {
      database_name: databaseName,
      schema_name: schemaName,
      limit: 500
    })
  }

  static async getTriggerDefinition(datasourceId: string, databaseName: string, triggerName: string) {
    return this.executeCommand(datasourceId, 'get_trigger_definition', {
      database_name: databaseName,
      trigger_name: triggerName
    })
  }

  // Views.
  static async getViews(datasourceId: string, databaseName: string, schemaName?: string) {
    return this.executeCommand(datasourceId, 'get_views', {
      database_name: databaseName,
      schema_name: schemaName,
      limit: 500
    })
  }

  static async getViewDefinition(datasourceId: string, databaseName: string, viewName: string, schemaName?: string) {
    return this.executeCommand(datasourceId, 'get_view_definition', {
      database_name: databaseName,
      view_name: viewName,
      schema_name: schemaName
    })
  }

  // Sequences.
  static async getSequences(datasourceId: string, databaseName: string, schemaName?: string) {
    return this.executeCommand(datasourceId, 'get_sequences', {
      database_name: databaseName,
      schema_name: schemaName,
      limit: 500
    })
  }

  // Constraints.
  static async getTableConstraints(datasourceId: string, databaseName: string, tableName: string, schemaName?: string) {
    return this.executeCommand(datasourceId, 'get_table_constraints', {
      database_name: databaseName,
      table_name: tableName,
      schema_name: schemaName
    })
  }

  static async addConstraint(
    datasourceId: string,
    databaseName: string,
    tableName: string,
    constraintData: {
      constraint_name: string
      constraint_type: 'PRIMARY KEY' | 'UNIQUE' | 'FOREIGN KEY' | 'CHECK'
      column_names: string[]
      referenced_table?: string
      referenced_columns?: string[]
      on_update?: string
      on_delete?: string
      check_expression?: string
    },
    schemaName?: string,
    suppressToast = false
  ) {
    return this.executeCommand(datasourceId, 'add_constraint', {
      database_name: databaseName,
      table_name: tableName,
      schema_name: schemaName,
      ...constraintData
    }, 30, suppressToast)
  }

  static async dropConstraint(
    datasourceId: string,
    databaseName: string,
    tableName: string,
    constraintName: string,
    schemaName?: string,
    suppressToast = false
  ) {
    return this.executeCommand(datasourceId, 'drop_constraint', {
      database_name: databaseName,
      table_name: tableName,
      schema_name: schemaName,
      constraint_name: constraintName
    }, 30, suppressToast)
  }

  static async renameColumn(
    datasourceId: string,
    databaseName: string,
    tableName: string,
    columnName: string,
    newColumnName: string,
    schemaName?: string,
    suppressToast = false
  ) {
    return this.executeCommand(datasourceId, 'rename_column', {
      database_name: databaseName,
      table_name: tableName,
      schema_name: schemaName,
      column_name: columnName,
      new_column_name: newColumnName
    }, 30, suppressToast)
  }

  // Database details.
  static async getDatabaseDetails(datasourceId: string, databaseName: string) {
    // Suppress popup toast for database details errors; let UI render inline errors.
    return this.executeCommand(
      datasourceId,
      'get_database_details',
      {
        database_name: databaseName
      },
      30,
      true
    )
  }

  // Test connection.
  /**
   * Test connection to a datasource.
   * @param datasourceId - The datasource ID.
   * @param agentId - Optional agentId if already known (avoids extra API call for encryption settings).
   * @param suppressToast - If true, suppress error toast notifications.
   */
  static async testConnection(datasourceId: string, agentId?: string, suppressToast = false) {
    return this.executeCommand(datasourceId, 'test_connection', {
      datasource_id: datasourceId
    }, 30, suppressToast, agentId)
  }

  // Additional operations for complete coverage.

  // Create operations.
  static async createFunction(datasourceId: string, databaseName: string, functionData: BaseCommandData, schemaName?: string) {
    return this.executeCommand(datasourceId, 'create_function', {
      database_name: databaseName,
      schema_name: schemaName,
      ...functionData
    })
  }

  static async createProcedure(datasourceId: string, databaseName: string, procedureData: BaseCommandData, schemaName?: string) {
    return this.executeCommand(datasourceId, 'create_procedure', {
      database_name: databaseName,
      schema_name: schemaName,
      ...procedureData
    })
  }

  static async createTrigger(datasourceId: string, databaseName: string, triggerData: BaseCommandData, schemaName?: string) {
    return this.executeCommand(datasourceId, 'create_trigger', {
      database_name: databaseName,
      schema_name: schemaName,
      ...triggerData
    })
  }

  static async createView(datasourceId: string, databaseName: string, viewData: BaseCommandData, schemaName?: string) {
    return this.executeCommand(datasourceId, 'create_view', {
      database_name: databaseName,
      schema_name: schemaName,
      ...viewData
    })
  }

  static async createSequence(datasourceId: string, databaseName: string, sequenceData: BaseCommandData) {
    return this.executeCommand(datasourceId, 'create_sequence', {
      database_name: databaseName,
      ...sequenceData
    })
  }

  // Drop operations.
  static async dropFunction(datasourceId: string, databaseName: string, functionName: string, schemaName?: string, functionArguments?: string) {
    return this.executeCommand(datasourceId, 'drop_function', {
      database_name: databaseName,
      function_name: functionName,
      schema_name: schemaName,
      ...(functionArguments ? { arguments: functionArguments } : {})
    })
  }

  static async dropProcedure(datasourceId: string, databaseName: string, procedureName: string, schemaName?: string) {
    return this.executeCommand(datasourceId, 'drop_procedure', {
      database_name: databaseName,
      procedure_name: procedureName,
      schema_name: schemaName
    })
  }

  static async dropTrigger(datasourceId: string, databaseName: string, triggerName: string, schemaName?: string, tableName?: string) {
    return this.executeCommand(datasourceId, 'drop_trigger', {
      database_name: databaseName,
      trigger_name: triggerName,
      cascade: true,
      ...(schemaName ? { schema_name: schemaName } : {}),
      ...(tableName ? { table_name: tableName } : {})
    })
  }

  static async dropView(datasourceId: string, databaseName: string, viewName: string, schemaName?: string, cascade?: boolean) {
    return this.executeCommand(datasourceId, 'drop_view', {
      database_name: databaseName,
      view_name: viewName,
      schema_name: schemaName,
      cascade: cascade
    })
  }

  static async dropSequence(datasourceId: string, databaseName: string, sequenceName: string, schemaName?: string, cascade?: boolean) {
    return this.executeCommand(datasourceId, 'drop_sequence', {
      database_name: databaseName,
      sequence_name: sequenceName,
      schema_name: schemaName || 'public',
      cascade: cascade
    })
  }

  // Alter operations.
  static async alterFunction(datasourceId: string, databaseName: string, functionName: string, functionData: BaseCommandData, schemaName?: string) {
    return this.executeCommand(datasourceId, 'alter_function', {
      database_name: databaseName,
      function_name: functionName,
      schema_name: schemaName,
      ...functionData
    })
  }

  static async alterProcedure(datasourceId: string, databaseName: string, procedureName: string, procedureData: BaseCommandData, schemaName?: string) {
    return this.executeCommand(datasourceId, 'alter_procedure', {
      database_name: databaseName,
      procedure_name: procedureName,
      schema_name: schemaName,
      ...procedureData
    })
  }

  static async alterTrigger(datasourceId: string, databaseName: string, triggerName: string, triggerData: BaseCommandData) {
    return this.executeCommand(datasourceId, 'alter_trigger', {
      database_name: databaseName,
      trigger_name: triggerName,
      ...triggerData
    })
  }

  static async alterView(datasourceId: string, databaseName: string, viewName: string, viewData: BaseCommandData, schemaName?: string) {
    return this.executeCommand(datasourceId, 'alter_view', {
      database_name: databaseName,
      view_name: viewName,
      schema_name: schemaName,
      ...viewData
    })
  }

  // Additional get operations removed duplicate.

  static async getSequenceDefinition(datasourceId: string, databaseName: string, sequenceName: string, schemaName?: string) {
    return this.executeCommand(datasourceId, 'get_sequence_definition', {
      database_name: databaseName,
      sequence_name: sequenceName,
      schema_name: schemaName || 'public'
    })
  }

  // Import/Export operations.
  static async importData(datasourceId: string, databaseName: string, importData: BaseCommandData) {
    return this.executeCommand(datasourceId, 'import_data', {
      database_name: databaseName,
      ...importData
    })
  }

  static async exportData(datasourceId: string, databaseName: string, exportData: BaseCommandData) {
    return this.executeCommand(datasourceId, 'export_data', {
      database_name: databaseName,
      ...exportData
    })
  }

  // Grant/Revoke privileges.
  static async grantPrivileges(datasourceId: string, databaseName: string, username: string, privileges: BaseCommandData) {
    return this.executeCommand(datasourceId, 'grant_privileges', {
      database_name: databaseName,
      username,
      ...privileges
    })
  }

  static async revokePrivileges(datasourceId: string, databaseName: string, username: string, privileges: BaseCommandData) {
    return this.executeCommand(datasourceId, 'revoke_privileges', {
      database_name: databaseName,
      username,
      ...privileges
    })
  }

  // Role management.
  static async getRoles(datasourceId: string, databaseName: string, limit?: number, offset?: number) {
    return this.executeCommand(datasourceId, 'get_roles', {
      database_name: databaseName,
      limit,
      offset
    })
  }

  static async createRole(datasourceId: string, databaseName: string, roleData: BaseCommandData) {
    console.log('🎭 Creating role with data:', {
      datasourceId,
      databaseName,
      roleData
    })

    const result = await this.executeCommand(datasourceId, 'create_role', {
      database_name: databaseName,
      ...roleData
    })

    console.log('🎭 Create role response:', result)
    return result
  }

  static async dropRole(datasourceId: string, databaseName: string, roleName: string) {
    return this.executeCommand(datasourceId, 'drop_role', {
      database_name: databaseName,
      role_name: roleName
    })
  }

  static async alterRole(datasourceId: string, databaseName: string, roleName: string, roleData: BaseCommandData) {
    return this.executeCommand(datasourceId, 'alter_role', {
      database_name: databaseName,
      role_name: roleName,
      ...roleData
    })
  }

  static async grantRole(datasourceId: string, databaseName: string, roleName: string, username: string) {
    return this.executeCommand(datasourceId, 'grant_role', {
      database_name: databaseName,
      role_name: roleName,
      username
    })
  }

  static async revokeRole(datasourceId: string, databaseName: string, roleName: string, username: string) {
    return this.executeCommand(datasourceId, 'revoke_role', {
      database_name: databaseName,
      role_name: roleName,
      username
    })
  }

  // Get grants for a user or role.
  static async getGrants(
    datasourceId: string,
    databaseName: string,
    username: string,
    granteeType?: 'user' | 'role',
    host?: string,
    suppressToast = false
  ) {
    return this.executeCommand(datasourceId, 'get_grants', {
      database_name: databaseName,
      username,
      grantee_type: granteeType,
      host
    }, 30, suppressToast)
  }

  // Default privileges management.
  static async getDefaultPrivileges(datasourceId: string, databaseName: string, schemaName?: string) {
    return this.executeCommand(datasourceId, 'get_default_privileges', {
      database_name: databaseName,
      schema_name: schemaName
    })
  }

  // Connection info.
  static async getConnectionInfo(datasourceId: string) {
    return this.executeCommand(datasourceId, 'get_connection_info', {})
  }

  // PostgreSQL Extension operations.
  static async getExtensions(datasourceId: string, databaseName: string, filter?: string, limit?: number, page?: number) {
    return this.executeCommand(datasourceId, 'get_extensions', {
      database_name: databaseName,
      filter,
      limit,
      page
    })
  }

  static async createExtension(datasourceId: string, databaseName: string, extensionName: string, options?: {
    schemaName?: string
    version?: string
  }) {
    return this.executeCommand(datasourceId, 'create_extension', {
      database_name: databaseName,
      extension_name: extensionName,
      schema_name: options?.schemaName,
      version: options?.version
    })
  }

  static async alterExtension(datasourceId: string, databaseName: string, extensionName: string, options?: {
    newVersion?: string
    newSchema?: string
  }) {
    return this.executeCommand(datasourceId, 'alter_extension', {
      database_name: databaseName,
      extension_name: extensionName,
      new_version: options?.newVersion,
      new_schema: options?.newSchema
    })
  }

  static async dropExtension(datasourceId: string, databaseName: string, extensionName: string, cascade?: boolean) {
    return this.executeCommand(datasourceId, 'drop_extension', {
      database_name: databaseName,
      extension_name: extensionName,
      cascade: cascade ?? false
    })
  }

  // ================== ClickHouse-Specific Operations ==================

  // Table Operations (ClickHouse-specific).
  static async optimizeTable(datasourceId: string, databaseName: string, tableName: string, partition?: string, final?: boolean) {
    return this.executeCommand(datasourceId, 'optimize_table', {
      database_name: databaseName,
      table_name: tableName,
      partition,
      final
    })
  }

  static async truncateTable(datasourceId: string, databaseName: string, tableName: string, schemaName?: string) {
    return this.executeCommand(datasourceId, 'truncate_table', {
      database_name: databaseName,
      table_name: tableName,
      schema_name: schemaName
    })
  }

  // System Operations.
  static async getMutationStatus(datasourceId: string, databaseName: string, tableName?: string) {
    return this.executeCommand(datasourceId, 'get_mutation_status', {
      database_name: databaseName,
      table_name: tableName
    })
  }

  static async killMutation(datasourceId: string, databaseName: string, mutationId: string) {
    return this.executeCommand(datasourceId, 'kill_mutation', {
      database_name: databaseName,
      mutation_id: mutationId
    })
  }

  static async getRunningQueries(datasourceId: string) {
    return this.executeCommand(datasourceId, 'get_running_queries', {})
  }

  static async getSystemMetrics(datasourceId: string) {
    return this.executeCommand(datasourceId, 'get_system_metrics', {})
  }

  static async getSystemEvents(datasourceId: string) {
    return this.executeCommand(datasourceId, 'get_system_events', {})
  }

  static async getAsynchronousMetrics(datasourceId: string) {
    return this.executeCommand(datasourceId, 'get_asynchronous_metrics', {})
  }

  static async getServerStatus(datasourceId: string) {
    return this.executeCommand(datasourceId, 'get_server_status', {})
  }

  static async getServerInfo(datasourceId: string) {
    return this.executeCommand(datasourceId, 'get_server_info', {})
  }

  // System Info Operations.
  static async getSettings(datasourceId: string) {
    return this.executeCommand(datasourceId, 'get_settings', {})
  }

  static async getPerformanceMetrics(datasourceId: string) {
    return this.executeCommand(datasourceId, 'get_performance_metrics', {})
  }

  static async getMergeTreeSettings(datasourceId: string) {
    return this.executeCommand(datasourceId, 'get_merge_tree_settings', {})
  }

  static async getTableEngines(datasourceId: string) {
    return this.executeCommand(datasourceId, 'get_table_engines', {})
  }

  static async getTableFunctions(datasourceId: string) {
    return this.executeCommand(datasourceId, 'get_table_functions', {})
  }

  static async getDataTypes(datasourceId: string) {
    return this.executeCommand(datasourceId, 'get_data_types', {})
  }

  static async getFormats(datasourceId: string) {
    return this.executeCommand(datasourceId, 'get_formats', {})
  }

  static async getStoragePolicies(datasourceId: string) {
    return this.executeCommand(datasourceId, 'get_storage_policies', {})
  }

  static async getDisks(datasourceId: string) {
    return this.executeCommand(datasourceId, 'get_disks', {})
  }

  static async getClusters(datasourceId: string) {
    return this.executeCommand(datasourceId, 'get_clusters', {})
  }

  // ===========================================
  // ScyllaDB-specific Operations
  // ===========================================

  /**
   * Create a ScyllaDB keyspace with replication strategy
   * @param datasourceId - The datasource ID
   * @param keyspaceName - Name of the keyspace to create
   * @param replication - Replication configuration
   * @param durableWrites - Whether to enable durable writes (default: true)
   */
  static async createKeyspace(
    datasourceId: string,
    keyspaceName: string,
    replication: {
      class: 'SimpleStrategy' | 'NetworkTopologyStrategy'
      replication_factor?: number
      // For NetworkTopologyStrategy: datacenter name -> replication factor
      [datacenter: string]: string | number | undefined
    },
    durableWrites = true
  ) {
    return this.executeCommand(datasourceId, 'create_database', {
      database_name: keyspaceName,
      replication,
      durable_writes: durableWrites
    })
  }

  /**
   * Get ScyllaDB keyspace details including replication info
   * @param datasourceId - The datasource ID
   * @param keyspaceName - Name of the keyspace
   */
  static async getKeyspaceDetails(datasourceId: string, keyspaceName: string) {
    return this.executeCommand(datasourceId, 'get_database_details', {
      database_name: keyspaceName
    })
  }

  /**
   * Create a ScyllaDB table with partition and clustering keys
   * @param datasourceId - The datasource ID
   * @param keyspaceName - Name of the keyspace
   * @param tableData - Table configuration including columns and keys
   */
  static async createScyllaDBTable(
    datasourceId: string,
    keyspaceName: string,
    tableData: {
      table_name: string
      columns: {
        name: string
        type: string
        primary_key?: boolean
        partition_key?: boolean
        clustering_key?: boolean
        clustering_order?: 'ASC' | 'DESC'
        static?: boolean
      }[]
      options?: {
        gc_grace_seconds?: number
        default_time_to_live?: number
        bloom_filter_fp_chance?: number
        compaction?: {
          class: string
          [key: string]: unknown
        }
        compression?: {
          sstable_compression?: string
          chunk_length_kb?: number
        }
        caching?: {
          keys?: 'ALL' | 'NONE'
          rows_per_partition?: 'ALL' | 'NONE' | number
        }
      }
    }
  ) {
    return this.executeCommand(datasourceId, 'create_table', {
      database_name: keyspaceName,
      ...tableData
    })
  }

  /**
   * Get ScyllaDB type definition
   * @param datasourceId - The datasource ID
   * @param keyspaceName - Name of the keyspace
   * @param typeName - Name of the type
   */
  static async getTypeDefinition(datasourceId: string, keyspaceName: string, typeName: string) {
    return this.executeCommand(datasourceId, 'get_type_definition', {
      database_name: keyspaceName,
      type_name: typeName
    })
  }

  /**
   * Create a ScyllaDB User Defined Type (UDT)
   * @param datasourceId - The datasource ID
   * @param keyspaceName - Name of the keyspace
   * @param typeName - Name of the type
   * @param fields - Array of field definitions
   */
  static async createType(
    datasourceId: string,
    keyspaceName: string,
    typeName: string,
    fields: { name: string; type: string }[]
  ) {
    return this.executeCommand(datasourceId, 'create_type', {
      database_name: keyspaceName,
      type_name: typeName,
      fields
    })
  }

  /**
   * Alter a ScyllaDB User Defined Type (UDT) - add a new field
   * @param datasourceId - The datasource ID
   * @param keyspaceName - Name of the keyspace
   * @param typeName - Name of the type
   * @param addField - New field to add
   */
  static async alterType(
    datasourceId: string,
    keyspaceName: string,
    typeName: string,
    addField: { name: string; type: string }
  ) {
    return this.executeCommand(datasourceId, 'alter_type', {
      database_name: keyspaceName,
      type_name: typeName,
      add_field: addField
    })
  }

  /**
   * Drop a ScyllaDB User Defined Type (UDT)
   * @param datasourceId - The datasource ID
   * @param keyspaceName - Name of the keyspace
   * @param typeName - Name of the type
   */
  static async dropType(datasourceId: string, keyspaceName: string, typeName: string) {
    return this.executeCommand(datasourceId, 'drop_type', {
      database_name: keyspaceName,
      type_name: typeName
    })
  }

  /**
   * Create a ScyllaDB User Defined Aggregate (UDA)
   * @param datasourceId - The datasource ID
   * @param keyspaceName - Name of the keyspace
   * @param aggregateData - Aggregate definition
   */
  static async createScyllaDBAggregate(
    datasourceId: string,
    keyspaceName: string,
    aggregateData: {
      aggregate_name: string
      argument_type: string
      state_func: string
      state_type: string
      final_func?: string
      init_cond?: string
      or_replace?: boolean
      if_not_exists?: boolean
    }
  ) {
    return this.executeCommand(datasourceId, 'create_aggregate', {
      database_name: keyspaceName,
      ...aggregateData
    })
  }

  /**
   * Drop a ScyllaDB User Defined Aggregate (UDA)
   * @param datasourceId - The datasource ID
   * @param keyspaceName - Name of the keyspace
   * @param aggregateName - Name of the aggregate
   * @param argumentType - Argument type to identify the aggregate signature
   */
  static async dropScyllaDBAggregate(
    datasourceId: string,
    keyspaceName: string,
    aggregateName: string,
    argumentType?: string
  ) {
    return this.executeCommand(datasourceId, 'drop_aggregate', {
      database_name: keyspaceName,
      aggregate_name: aggregateName,
      argument_type: argumentType
    })
  }

  /**
   * Get ScyllaDB aggregate definition
   * @param datasourceId - The datasource ID
   * @param keyspaceName - Name of the keyspace
   * @param aggregateName - Name of the aggregate
   */
  static async getAggregateDefinition(datasourceId: string, keyspaceName: string, aggregateName: string) {
    return this.executeCommand(datasourceId, 'get_aggregate_definition', {
      database_name: keyspaceName,
      aggregate_name: aggregateName
    })
  }

  /**
   * Get ScyllaDB materialized views for a keyspace
   * @param datasourceId - The datasource ID
   * @param keyspaceName - Name of the keyspace
   */
  static async getMaterializedViews(datasourceId: string, keyspaceName: string) {
    return this.executeCommand(datasourceId, 'get_views', {
      database_name: keyspaceName,
      view_type: 'materialized'
    })
  }

  /**
   * Create a ScyllaDB materialized view with CQL query
   * @param datasourceId - The datasource ID
   * @param keyspaceName - Name of the keyspace
   * @param viewName - Name of the view
   * @param query - CQL CREATE MATERIALIZED VIEW statement
   */
  static async createScyllaDBMaterializedView(
    datasourceId: string,
    keyspaceName: string,
    viewName: string,
    query: string
  ) {
    return this.executeCommand(datasourceId, 'create_view', {
      database_name: keyspaceName,
      view_name: viewName,
      query
    })
  }

  /**
   * Prune a ScyllaDB materialized view
   * @param datasourceId - The datasource ID
   * @param keyspaceName - Name of the keyspace
   * @param viewName - Name of the materialized view
   */
  static async pruneMaterializedView(
    datasourceId: string,
    keyspaceName: string,
    viewName: string
  ) {
    return this.executeCommand(datasourceId, 'prune_materialized_view', {
      database_name: keyspaceName,
      view_name: viewName
    })
  }

  /**
   * Get table storage options (ScyllaDB)
   * @param datasourceId - The datasource ID
   * @param keyspaceName - Name of the keyspace
   * @param tableName - Name of the table
   */
  static async getTableStorageOptions(
    datasourceId: string,
    keyspaceName: string,
    tableName: string
  ) {
    return this.executeCommand(datasourceId, 'get_table_storage_options', {
      database_name: keyspaceName,
      table_name: tableName
    })
  }

  /**
   * Get CDC-enabled tables in a keyspace (ScyllaDB)
   * @param datasourceId - The datasource ID
   * @param keyspaceName - Name of the keyspace
   */
  static async getCDCTables(datasourceId: string, keyspaceName: string) {
    return this.executeCommand(datasourceId, 'get_cdc_tables', {
      database_name: keyspaceName
    })
  }

  /**
   * Enable CDC on a table (ScyllaDB)
   * @param datasourceId - The datasource ID
   * @param keyspaceName - Name of the keyspace
   * @param tableName - Name of the table
   * @param options - CDC options (preimage, postimage, ttl)
   */
  static async enableCDC(
    datasourceId: string,
    keyspaceName: string,
    tableName: string,
    options?: { preimage?: boolean; postimage?: boolean; ttl?: number }
  ) {
    return this.executeCommand(datasourceId, 'enable_cdc', {
      database_name: keyspaceName,
      table_name: tableName,
      ...options
    })
  }

  /**
   * Disable CDC on a table (ScyllaDB)
   * @param datasourceId - The datasource ID
   * @param keyspaceName - Name of the keyspace
   * @param tableName - Name of the table
   */
  static async disableCDC(
    datasourceId: string,
    keyspaceName: string,
    tableName: string
  ) {
    return this.executeCommand(datasourceId, 'disable_cdc', {
      database_name: keyspaceName,
      table_name: tableName
    })
  }

  /**
   * Get CDC log entries for a table (ScyllaDB)
   * @param datasourceId - The datasource ID
   * @param keyspaceName - Name of the keyspace
   * @param tableName - Name of the table
   * @param limit - Maximum number of entries to return
   */
  static async getCDCLog(
    datasourceId: string,
    keyspaceName: string,
    tableName: string,
    limit?: number
  ) {
    return this.executeCommand(datasourceId, 'get_cdc_log', {
      database_name: keyspaceName,
      table_name: tableName,
      ...(limit && { limit })
    })
  }

  /**
   * Get all service levels (ScyllaDB)
   * @param datasourceId - The datasource ID
   * @param keyspaceName - Name of the keyspace context
   */
  static async getServiceLevels(datasourceId: string, keyspaceName: string) {
    return this.executeCommand(datasourceId, 'get_service_levels', {
      database_name: keyspaceName
    })
  }

  /**
   * Create a service level (ScyllaDB)
   * @param datasourceId - The datasource ID
   * @param keyspaceName - Name of the keyspace context
   * @param serviceLevelName - Name of the service level
   * @param options - Service level options (timeout, workload_type)
   */
  static async createServiceLevel(
    datasourceId: string,
    keyspaceName: string,
    serviceLevelName: string,
    options?: { timeout?: string; workload_type?: 'unspecified' | 'interactive' | 'batch' }
  ) {
    return this.executeCommand(datasourceId, 'create_service_level', {
      database_name: keyspaceName,
      service_level_name: serviceLevelName,
      ...options
    })
  }

  /**
   * Alter a service level (ScyllaDB)
   * @param datasourceId - The datasource ID
   * @param keyspaceName - Name of the keyspace context
   * @param serviceLevelName - Name of the service level
   * @param options - Service level options to update
   */
  static async alterServiceLevel(
    datasourceId: string,
    keyspaceName: string,
    serviceLevelName: string,
    options?: { timeout?: string; workload_type?: 'unspecified' | 'interactive' | 'batch' }
  ) {
    return this.executeCommand(datasourceId, 'alter_service_level', {
      database_name: keyspaceName,
      service_level_name: serviceLevelName,
      ...options
    })
  }

  /**
   * Drop a service level (ScyllaDB)
   * @param datasourceId - The datasource ID
   * @param keyspaceName - Name of the keyspace context
   * @param serviceLevelName - Name of the service level to drop
   */
  static async dropServiceLevel(
    datasourceId: string,
    keyspaceName: string,
    serviceLevelName: string
  ) {
    return this.executeCommand(datasourceId, 'drop_service_level', {
      database_name: keyspaceName,
      service_level_name: serviceLevelName
    })
  }

  /**
   * Attach a service level to a role (ScyllaDB)
   * @param datasourceId - The datasource ID
   * @param keyspaceName - Name of the keyspace context
   * @param serviceLevelName - Name of the service level
   * @param roleName - Name of the role
   */
  static async attachServiceLevel(
    datasourceId: string,
    keyspaceName: string,
    serviceLevelName: string,
    roleName: string
  ) {
    return this.executeCommand(datasourceId, 'attach_service_level', {
      database_name: keyspaceName,
      service_level_name: serviceLevelName,
      role_name: roleName
    })
  }

  /**
   * Detach a service level from a role (ScyllaDB)
   * @param datasourceId - The datasource ID
   * @param keyspaceName - Name of the keyspace context
   * @param roleName - Name of the role
   */
  static async detachServiceLevel(
    datasourceId: string,
    keyspaceName: string,
    roleName: string
  ) {
    return this.executeCommand(datasourceId, 'detach_service_level', {
      database_name: keyspaceName,
      role_name: roleName
    })
  }

  /**
   * Set compaction strategy for a table (ScyllaDB)
   * @param datasourceId - The datasource ID
   * @param keyspaceName - Name of the keyspace
   * @param tableName - Name of the table
   * @param strategy - Compaction strategy class name
   * @param options - Strategy-specific options
   */
  static async setCompactionStrategy(
    datasourceId: string,
    keyspaceName: string,
    tableName: string,
    strategy: string,
    options?: Record<string, unknown>
  ) {
    return this.executeCommand(datasourceId, 'set_compaction_strategy', {
      database_name: keyspaceName,
      table_name: tableName,
      strategy,
      ...options
    })
  }

  /**
   * Set compression for a table (ScyllaDB)
   * @param datasourceId - The datasource ID
   * @param keyspaceName - Name of the keyspace
   * @param tableName - Name of the table
   * @param compression - Compression settings
   */
  static async setCompression(
    datasourceId: string,
    keyspaceName: string,
    tableName: string,
    compression: { class?: string; chunk_length_kb?: number; enabled?: boolean }
  ) {
    return this.executeCommand(datasourceId, 'set_compression', {
      database_name: keyspaceName,
      table_name: tableName,
      ...compression
    })
  }

  /**
   * Set default TTL for a table (ScyllaDB)
   * @param datasourceId - The datasource ID
   * @param keyspaceName - Name of the keyspace
   * @param tableName - Name of the table
   * @param ttl - TTL in seconds
   */
  static async setDefaultTTL(
    datasourceId: string,
    keyspaceName: string,
    tableName: string,
    ttl: number
  ) {
    return this.executeCommand(datasourceId, 'set_default_ttl', {
      database_name: keyspaceName,
      table_name: tableName,
      ttl_seconds: ttl
    })
  }

  /**
   * Set GC grace seconds for a table (ScyllaDB)
   * @param datasourceId - The datasource ID
   * @param keyspaceName - Name of the keyspace
   * @param tableName - Name of the table
   * @param seconds - GC grace period in seconds
   */
  static async setGCGraceSeconds(
    datasourceId: string,
    keyspaceName: string,
    tableName: string,
    seconds: number
  ) {
    return this.executeCommand(datasourceId, 'set_gc_grace_seconds', {
      database_name: keyspaceName,
      table_name: tableName,
      gc_grace_seconds: seconds
    })
  }

  /**
   * Set speculative retry for a table (ScyllaDB)
   * @param datasourceId - The datasource ID
   * @param keyspaceName - Name of the keyspace
   * @param tableName - Name of the table
   * @param setting - Speculative retry setting (e.g., "99percentile", "10ms", "NONE")
   */
  static async setSpeculativeRetry(
    datasourceId: string,
    keyspaceName: string,
    tableName: string,
    setting: string
  ) {
    return this.executeCommand(datasourceId, 'set_speculative_retry', {
      database_name: keyspaceName,
      table_name: tableName,
      speculative_retry: setting
    })
  }

  /**
   * Set read repair mode for a table (ScyllaDB)
   * @param datasourceId - The datasource ID
   * @param keyspaceName - Name of the keyspace
   * @param tableName - Name of the table
   * @param mode - Read repair mode (BLOCKING or NONE)
   */
  static async setReadRepair(
    datasourceId: string,
    keyspaceName: string,
    tableName: string,
    mode: 'BLOCKING' | 'NONE'
  ) {
    return this.executeCommand(datasourceId, 'set_read_repair', {
      database_name: keyspaceName,
      table_name: tableName,
      read_repair: mode
    })
  }

  // =====================================================
  // ScyllaDB Cluster Monitoring Operations
  // =====================================================

  /**
   * Get ScyllaDB cluster information (version, DC, rack, partitioner, etc.)
   * @param datasourceId - The datasource ID
   */
  static async getClusterInfo(datasourceId: string) {
    return this.executeCommand(datasourceId, 'get_cluster_info', {})
  }

  /**
   * Get system.local table data
   * @param datasourceId - The datasource ID
   */
  static async getSystemLocal(datasourceId: string) {
    return this.executeCommand(datasourceId, 'get_system_local', {})
  }

  /**
   * Get system.peers table data
   * @param datasourceId - The datasource ID
   */
  static async getSystemPeers(datasourceId: string) {
    return this.executeCommand(datasourceId, 'get_system_peers', {})
  }

  /**
   * Get cluster status with node health information
   * @param datasourceId - The datasource ID
   */
  static async getClusterStatus(datasourceId: string) {
    return this.executeCommand(datasourceId, 'get_cluster_status', {})
  }

  // ========================
  // Milvus-Specific Methods
  // ========================

  static async loadCollection(datasourceId: string, tableName: string, databaseName?: string) {
    return this.executeCommand(datasourceId, 'load_collection', {
      table_name: tableName,
      ...(databaseName && { database_name: databaseName }),
    }, 120, true)
  }

  static async releaseCollection(datasourceId: string, tableName: string, databaseName?: string) {
    return this.executeCommand(datasourceId, 'release_collection', {
      table_name: tableName,
      ...(databaseName && { database_name: databaseName }),
    }, 30, true)
  }

  static async getCollectionLoadState(datasourceId: string, tableName: string, databaseName?: string) {
    return this.executeCommand(datasourceId, 'get_collection_load_state', {
      table_name: tableName,
      ...(databaseName && { database_name: databaseName }),
    })
  }

  static async getCollectionStats(datasourceId: string, tableName: string, databaseName?: string) {
    return this.executeCommand(datasourceId, 'get_collection_stats', {
      table_name: tableName,
      ...(databaseName && { database_name: databaseName }),
    })
  }

  static async flushCollection(datasourceId: string, tableName: string, databaseName?: string) {
    return this.executeCommand(datasourceId, 'flush_collection', {
      table_name: tableName,
      ...(databaseName && { database_name: databaseName }),
    }, 60, true)
  }

  static async compactCollection(datasourceId: string, tableName: string, databaseName?: string) {
    return this.executeCommand(datasourceId, 'compact_collection', {
      table_name: tableName,
      ...(databaseName && { database_name: databaseName }),
    }, 120, true)
  }

  static async vectorSearch(datasourceId: string, data: BaseCommandData) {
    return this.executeCommand(datasourceId, 'vector_search', data, 30)
  }

  static async hybridSearch(datasourceId: string, data: BaseCommandData) {
    return this.executeCommand(datasourceId, 'hybrid_search', data, 30)
  }

  static async getMilvusPartitions(datasourceId: string, tableName: string, databaseName?: string) {
    return this.executeCommand(datasourceId, 'get_partitions', {
      table_name: tableName,
      ...(databaseName && { database_name: databaseName }),
    })
  }

  static async createMilvusPartition(datasourceId: string, tableName: string, partitionName: string, databaseName?: string) {
    return this.executeCommand(datasourceId, 'create_partition', {
      table_name: tableName,
      partition_name: partitionName,
      ...(databaseName && { database_name: databaseName }),
    })
  }

  static async dropMilvusPartition(datasourceId: string, tableName: string, partitionName: string, databaseName?: string) {
    return this.executeCommand(datasourceId, 'drop_partition', {
      table_name: tableName,
      partition_name: partitionName,
      ...(databaseName && { database_name: databaseName }),
    })
  }

  static async listMilvusAliases(datasourceId: string, tableName?: string, databaseName?: string) {
    return this.executeCommand(datasourceId, 'list_aliases', {
      ...(tableName && { table_name: tableName }),
      ...(databaseName && { database_name: databaseName }),
    })
  }

  static async createMilvusAlias(datasourceId: string, tableName: string, aliasName: string, databaseName?: string) {
    return this.executeCommand(datasourceId, 'create_alias', {
      table_name: tableName,
      alias_name: aliasName,
      ...(databaseName && { database_name: databaseName }),
    })
  }

  static async dropMilvusAlias(datasourceId: string, aliasName: string, databaseName?: string) {
    return this.executeCommand(datasourceId, 'drop_alias', {
      alias_name: aliasName,
      ...(databaseName && { database_name: databaseName }),
    })
  }

  static async getMilvusServerStatus(datasourceId: string) {
    return this.executeCommand(datasourceId, 'get_server_status', {})
  }

  static async getMilvusServerVariables(datasourceId: string) {
    return this.executeCommand(datasourceId, 'get_server_variables', {})
  }

  static async executeMilvusQuery(datasourceId: string, data: BaseCommandData) {
    return this.executeCommand(datasourceId, 'execute_query', data, 30)
  }

  static async countMilvusEntities(datasourceId: string, tableName: string, filter?: string, databaseName?: string, suppressToast = false) {
    return this.executeCommand(datasourceId, 'count_entities', {
      table_name: tableName,
      ...(filter && { filter }),
      ...(databaseName && { database_name: databaseName }),
    }, 30, suppressToast)
  }

  static async alterMilvusAlias(datasourceId: string, tableName: string, aliasName: string, databaseName?: string) {
    return this.executeCommand(datasourceId, 'alter_alias', {
      table_name: tableName,
      alias_name: aliasName,
      ...(databaseName && { database_name: databaseName }),
    })
  }

  static async describeMilvusAlias(datasourceId: string, aliasName: string, databaseName?: string) {
    return this.executeCommand(datasourceId, 'describe_alias', {
      alias_name: aliasName,
      ...(databaseName && { database_name: databaseName }),
    })
  }

  static async rangeSearch(datasourceId: string, data: BaseCommandData) {
    return this.executeCommand(datasourceId, 'range_search', data, 30)
  }

  static async fullTextSearch(datasourceId: string, data: BaseCommandData) {
    return this.executeCommand(datasourceId, 'full_text_search', data, 30)
  }

  static async sparseVectorSearch(datasourceId: string, data: BaseCommandData) {
    return this.executeCommand(datasourceId, 'sparse_vector_search', data, 30)
  }

  static async multiVectorSearch(datasourceId: string, data: BaseCommandData) {
    return this.executeCommand(datasourceId, 'hybrid_search', data, 60)
  }

  static async searchIterator(datasourceId: string, data: BaseCommandData) {
    return this.executeCommand(datasourceId, 'search_iterator', data, 60)
  }

  static async addMilvusColumn(datasourceId: string, tableName: string, column: Record<string, unknown>, databaseName?: string) {
    return this.executeCommand(datasourceId, 'add_column', {
      table_name: tableName,
      column,
      ...(databaseName && { database_name: databaseName }),
    })
  }

  static async alterCollection(datasourceId: string, tableName: string, properties: Record<string, unknown>, databaseName?: string) {
    return this.executeCommand(datasourceId, 'alter_collection', {
      table_name: tableName,
      ...properties,
      ...(databaseName && { database_name: databaseName }),
    })
  }

  static async alterCollectionField(datasourceId: string, tableName: string, fieldName: string, properties: Record<string, unknown>, databaseName?: string) {
    return this.executeCommand(datasourceId, 'alter_collection_field', {
      table_name: tableName,
      field_name: fieldName,
      ...properties,
      ...(databaseName && { database_name: databaseName }),
    })
  }

  static async getCompactionState(datasourceId: string, tableName: string, databaseName?: string) {
    return this.executeCommand(datasourceId, 'get_compaction_state', {
      table_name: tableName,
      ...(databaseName && { database_name: databaseName }),
    })
  }

  static async getIndexBuildProgress(datasourceId: string, tableName: string, fieldName: string, databaseName?: string) {
    return this.executeCommand(datasourceId, 'get_index_build_progress', {
      table_name: tableName,
      field_name: fieldName,
      ...(databaseName && { database_name: databaseName }),
    })
  }

  static async describeIndex(datasourceId: string, tableName: string, fieldName: string, databaseName?: string) {
    return this.executeCommand(datasourceId, 'describe_index', {
      table_name: tableName,
      field_name: fieldName,
      ...(databaseName && { database_name: databaseName }),
    })
  }

  static async getEntitiesByPk(datasourceId: string, tableName: string, ids: (string | number)[], outputFields?: string[], databaseName?: string) {
    return this.executeCommand(datasourceId, 'get_entities_by_pk', {
      table_name: tableName,
      ids,
      ...(outputFields && { output_fields: outputFields }),
      ...(databaseName && { database_name: databaseName }),
    })
  }

  static async loadPartitions(datasourceId: string, tableName: string, partitionNames: string[], databaseName?: string) {
    return this.executeCommand(datasourceId, 'load_partitions', {
      table_name: tableName,
      partition_names: partitionNames,
      ...(databaseName && { database_name: databaseName }),
    }, 120)
  }

  static async releasePartitions(datasourceId: string, tableName: string, partitionNames: string[], databaseName?: string) {
    return this.executeCommand(datasourceId, 'release_partitions', {
      table_name: tableName,
      partition_names: partitionNames,
      ...(databaseName && { database_name: databaseName }),
    }, 30)
  }

  static async getPartitionStats(datasourceId: string, tableName: string, partitionName: string, databaseName?: string) {
    return this.executeCommand(datasourceId, 'get_partition_stats', {
      table_name: tableName,
      partition_name: partitionName,
      ...(databaseName && { database_name: databaseName }),
    })
  }

  static async insertIntoPartition(datasourceId: string, tableName: string, partitionName: string, rows: Record<string, unknown>[], databaseName?: string) {
    return this.executeCommand(datasourceId, 'insert_into_partition', {
      table_name: tableName,
      partition_name: partitionName,
      rows,
      ...(databaseName && { database_name: databaseName }),
    }, 60)
  }

  static async queryPartition(datasourceId: string, tableName: string, partitionName: string, filter?: string, outputFields?: string[], limit?: number, databaseName?: string) {
    return this.executeCommand(datasourceId, 'query_partition', {
      table_name: tableName,
      partition_name: partitionName,
      ...(filter && { filter }),
      ...(outputFields && { output_fields: outputFields }),
      ...(limit && { limit }),
      ...(databaseName && { database_name: databaseName }),
    }, 30)
  }

  static async importMilvusData(datasourceId: string, tableName: string, files: string[], partitionName?: string, databaseName?: string) {
    return this.executeCommand(datasourceId, 'import_data', {
      table_name: tableName,
      files,
      ...(partitionName && { partition_name: partitionName }),
      ...(databaseName && { database_name: databaseName }),
    }, 120)
  }

  static async exportMilvusData(datasourceId: string, tableName: string, outputFields?: string[], filter?: string, limit?: number, databaseName?: string) {
    return this.executeCommand(datasourceId, 'export_data', {
      table_name: tableName,
      ...(outputFields && { output_fields: outputFields }),
      ...(filter && { filter }),
      ...(limit && { limit }),
      ...(databaseName && { database_name: databaseName }),
    }, 120)
  }

  static async listResourceGroups(datasourceId: string) {
    return this.executeCommand(datasourceId, 'list_resource_groups', {})
  }

  static async createResourceGroup(datasourceId: string, name: string, config?: Record<string, unknown>) {
    return this.executeCommand(datasourceId, 'create_resource_group', {
      name,
      ...(config && { config }),
    })
  }

  static async dropResourceGroup(datasourceId: string, name: string) {
    return this.executeCommand(datasourceId, 'drop_resource_group', { name })
  }

  static async describeResourceGroup(datasourceId: string, name: string) {
    return this.executeCommand(datasourceId, 'describe_resource_group', { name })
  }

  static async updateResourceGroup(datasourceId: string, name: string, config: Record<string, unknown>) {
    return this.executeCommand(datasourceId, 'update_resource_group', {
      name,
      ...config,
    })
  }

  static async listPrivilegeGroups(datasourceId: string) {
    return this.executeCommand(datasourceId, 'list_privilege_groups', {})
  }

  static async createPrivilegeGroup(datasourceId: string, name: string) {
    return this.executeCommand(datasourceId, 'create_privilege_group', { name })
  }

  static async dropPrivilegeGroup(datasourceId: string, name: string) {
    return this.executeCommand(datasourceId, 'drop_privilege_group', { name })
  }

  static async getReplicas(datasourceId: string, tableName: string, databaseName?: string) {
    return this.executeCommand(datasourceId, 'get_replicas', {
      table_name: tableName,
      ...(databaseName && { database_name: databaseName }),
    })
  }

  static async copyCollection(datasourceId: string, tableName: string, newTableName: string, databaseName?: string) {
    return this.executeCommand(datasourceId, 'copy_table', {
      table_name: tableName,
      new_table_name: newTableName,
      ...(databaseName && { database_name: databaseName }),
    }, 120)
  }

  static async setConsistencyLevel(datasourceId: string, level: string) {
    return this.executeCommand(datasourceId, 'set_consistency_level', { level })
  }

  static async getLoadingProgress(datasourceId: string, tableName: string, databaseName?: string) {
    return this.executeCommand(datasourceId, 'get_loading_progress', {
      table_name: tableName,
      ...(databaseName && { database_name: databaseName }),
    })
  }

  // Milvus - Database Operations
  static async describeDatabase(datasourceId: string, databaseName: string) {
    return this.executeCommand(datasourceId, 'describe_database', {
      database_name: databaseName,
    })
  }

  static async alterDatabase(datasourceId: string, databaseName: string, properties: Record<string, unknown>) {
    return this.executeCommand(datasourceId, 'alter_database', {
      database_name: databaseName,
      properties,
    })
  }

  static async useDatabase(datasourceId: string, databaseName: string) {
    return this.executeCommand(datasourceId, 'use_database', {
      database_name: databaseName,
    })
  }

  // Milvus - Data Operations
  static async upsertRows(datasourceId: string, databaseName: string, tableName: string, rows: Record<string, unknown>[]) {
    return this.executeCommand(datasourceId, 'upsert_rows', {
      table_name: tableName,
      rows,
      ...(databaseName && { database_name: databaseName }),
    }, 60)
  }

  static async queryIterator(datasourceId: string, data: BaseCommandData) {
    return this.executeCommand(datasourceId, 'query_iterator', data, 60)
  }

  // Milvus - Partition Operations
  static async hasPartition(datasourceId: string, tableName: string, partitionName: string, databaseName?: string) {
    return this.executeCommand(datasourceId, 'has_partition', {
      table_name: tableName,
      partition_name: partitionName,
      ...(databaseName && { database_name: databaseName }),
    })
  }

  // Milvus - RBAC Operations
  static async describeRole(datasourceId: string, databaseName: string, roleName: string) {
    return this.executeCommand(datasourceId, 'describe_role', {
      role_name: roleName,
      ...(databaseName && { database_name: databaseName }),
    })
  }

  // Milvus - Resource Group Operations
  static async transferNode(datasourceId: string, sourceGroup: string, targetGroup: string, numNodes: number) {
    return this.executeCommand(datasourceId, 'transfer_node', {
      source_resource_group: sourceGroup,
      target_resource_group: targetGroup,
      num_nodes: numNodes,
    })
  }

  static async transferReplica(datasourceId: string, sourceGroup: string, targetGroup: string, collectionName: string, numReplicas: number) {
    return this.executeCommand(datasourceId, 'transfer_replica', {
      source_resource_group: sourceGroup,
      target_resource_group: targetGroup,
      collection_name: collectionName,
      num_replicas: numReplicas,
    })
  }

  // Milvus - Privilege Group Operations
  static async addPrivilegesToGroup(datasourceId: string, groupName: string, privileges: string[]) {
    return this.executeCommand(datasourceId, 'add_privileges_to_group', {
      group_name: groupName,
      privileges,
    })
  }

  static async removePrivilegesFromGroup(datasourceId: string, groupName: string, privileges: string[]) {
    return this.executeCommand(datasourceId, 'remove_privileges_from_group', {
      group_name: groupName,
      privileges,
    })
  }

  // Milvus - Collection Operations
  static async renameCollection(datasourceId: string, oldName: string, newName: string, databaseName?: string) {
    return this.executeCommand(datasourceId, 'rename_collection', {
      table_name: oldName,
      new_name: newName,
      ...(databaseName && { database_name: databaseName }),
    })
  }

  static async dropCollectionProperties(datasourceId: string, tableName: string, propertyKeys: string[], databaseName?: string) {
    return this.executeCommand(datasourceId, 'drop_collection_properties', {
      table_name: tableName,
      property_keys: propertyKeys,
      ...(databaseName && { database_name: databaseName }),
    })
  }

  static async vacuumTable(datasourceId: string, tableName: string, databaseName?: string) {
    return this.executeCommand(datasourceId, 'vacuum_table', {
      table_name: tableName,
      ...(databaseName && { database_name: databaseName }),
    }, 120)
  }

  static async getFlushState(datasourceId: string, tableName: string, databaseName?: string) {
    return this.executeCommand(datasourceId, 'get_flush_state', {
      table_name: tableName,
      ...(databaseName && { database_name: databaseName }),
    })
  }

  // Milvus - Index Operations
  static async alterIndexProperties(datasourceId: string, tableName: string, indexName: string, properties: Record<string, unknown>, databaseName?: string) {
    return this.executeCommand(datasourceId, 'alter_index_properties', {
      table_name: tableName,
      index_name: indexName,
      properties,
      ...(databaseName && { database_name: databaseName }),
    })
  }

  static async dropIndexProperties(datasourceId: string, tableName: string, indexName: string, propertyKeys: string[], databaseName?: string) {
    return this.executeCommand(datasourceId, 'drop_index_properties', {
      table_name: tableName,
      field_name: indexName,
      property_keys: propertyKeys,
      ...(databaseName && { database_name: databaseName }),
    })
  }
}
